import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import FolderCard from '@/components/qr/FolderCard';
import { getProjectBySlug } from '@/projects';
import ExplorerBar from '@/components/explorer/ExplorerBar';
import PinGate from '@/components/auth/PinGate';
import { isUnlocked, setUnlocked, verifyQrTicket } from '@/lib/access';
import { useTranslation } from 'react-i18next';
import { getLocalizedText, normalizeLang, sanitizeText } from '@/lib/format';

const ProjectFolders: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const qr = searchParams.get('qr');
  const ticket = searchParams.get('t') || '';

  const project = slug ? getProjectBySlug(slug) : undefined;
  const [authorized, setAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);
  const [ticketError, setTicketError] = useState<string | null>(null);

  const cards = useMemo(() => {
    if (!project) return [];
    const entries: { kind: 'images' | 'videos' | 'panos'; count: number }[] = [];
    if (project.assets.images.length > 0) entries.push({ kind: 'images', count: project.assets.images.length });
    if (project.assets.videos.length > 0) entries.push({ kind: 'videos', count: project.assets.videos.length });
    if (project.assets.panos.length > 0) entries.push({ kind: 'panos', count: project.assets.panos.length });
    return entries;
  }, [project]);

  useEffect(() => {
    let mounted = true;
    const checkAccess = async () => {
      if (!slug || !project) {
        setChecking(false);
        return;
      }

      const unlocked = await isUnlocked(slug);
      if (!mounted) return;
      if (unlocked) {
        setAuthorized(true);
        setChecking(false);
        return;
      }

      if (qr === '1' && ticket) {
        if (verifyQrTicket(project, ticket)) {
          await setUnlocked(slug, 'ticket');
          if (!mounted) return;
          setAuthorized(true);
          setChecking(false);
          navigate(location.pathname, { replace: true });
          return;
        }
        setTicketError(t('pinGate.ticketError'));
      }

      setChecking(false);
    };

    checkAccess();
    return () => {
      mounted = false;
    };
  }, [slug, project, qr, ticket, location.pathname, navigate, t]);

  if (!project) {
    return (
      <MainLayout>
        <div className="pt-32 pb-24 bg-[#2A2A2A]">
          <div className="container-custom text-center text-[#E0E0E0]">
            <p className="text-xl">{t('common.projectNotFound')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (checking) {
    return (
      <MainLayout>
        <div className="pt-32 pb-24 bg-[#2A2A2A] min-h-screen">
          <div className="container-custom text-center text-[#E0E0E0]">{t('common.checkingAccess')}</div>
        </div>
      </MainLayout>
    );
  }

  const breadcrumbs = [
    { label: t('common.explorer'), to: `/p/${project.slug}` },
    { label: project.info.title, to: `/p/${project.slug}` }
  ];

  if (!authorized) {
    return (
      <MainLayout>
        <PinGate
          slug={project.slug}
          projectTitle={project.info.title}
          message={ticketError || undefined}
          onSuccess={() => setAuthorized(true)}
        />
      </MainLayout>
    );
  }

  const lang = normalizeLang(i18n.language);
  const formattedYear = new Intl.NumberFormat(lang).format(project.info.year);
  const category = getLocalizedText(project.info.category, lang);

  return (
    <MainLayout>
      <div className="pt-28 sm:pt-32 pb-20 bg-[#2A2A2A] min-h-screen">
        <div className="container-custom space-y-8 animate-[fadeIn_0.2s_ease]">
          <ExplorerBar breadcrumbs={breadcrumbs} />
          <div className="flex flex-col gap-2">
            <p className="text-sm uppercase tracking-wide text-[#AAAAAA]">{t('common.project')}</p>
            <h1 className="text-3xl sm:text-4xl font-semibold text-[#E0E0E0]">{project.info.title}</h1>
            <p className="text-[#B5B5B5]">{sanitizeText(category)} / {formattedYear}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {cards.length === 0 && (
              <div className="text-[#E0E0E0] bg-[#232323] rounded-xl p-6">{t('common.noFiles')}</div>
            )}
            {cards.map((card) => (
              <FolderCard
                key={card.kind}
                slug={project.slug}
                kind={card.kind}
                count={card.count}
              />
            ))}
          </div>

          <div className="text-sm text-[#888888]">
            {t('common.directLinks')}:
            <ul className="mt-2 space-y-1 text-[#E0E0E0]">
              {cards.map((card) => (
                <li key={card.kind}>
                  <a
                    href={`/p/${project.slug}/${card.kind}`}
                    className="text-[#C8102E] hover:underline"
                  >
                    /p/{project.slug}/{card.kind}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ProjectFolders;
